import pdfMake from "pdfmake/build/pdfmake.js";

import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";

import { useEffect } from "react";
import { useParams } from "react-router-dom";

import { fetchDetails } from "../slices/detailsSlice";
import { fetchUpdates } from "../slices/updatesSlice";
import TimesRegUrl from "../assets/fonts/TimesNewRoman.ttf";
import TimesBoldUrl from "../assets/fonts/TimesNewRoman-Bold.ttf";
import TimesItUrl from "../assets/fonts/TimesNewRoman-Italic.ttf";
import TimesBoldItUrl from "../assets/fonts/TimesNewRoman-BoldItalic.ttf";

let TIMES_READY = false;
let TIMES_VFS = null;
let TIMES_FONTS = null;

const fetchAsBase64Raw = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return await new Promise((resolve) => {
        const r = new FileReader();
        r.onloadend = () => resolve(String(r.result).split(",")[1]); // чистый base64
        r.readAsDataURL(blob);
    });
};

const ensureTimes = async () => {
    if (TIMES_READY && TIMES_VFS && TIMES_FONTS) {
        return { fonts: TIMES_FONTS, vfs: TIMES_VFS };
    }

    const [reg, bold, it, bi] = await Promise.all([
        fetchAsBase64Raw(TimesRegUrl),
        fetchAsBase64Raw(TimesBoldUrl),
        fetchAsBase64Raw(TimesItUrl),
        fetchAsBase64Raw(TimesBoldItUrl),
    ]);

    const vfs = {
        "TimesNewRoman.ttf": reg,
        "TimesNewRoman-Bold.ttf": bold,
        "TimesNewRoman-Italic.ttf": it,
        "TimesNewRoman-BoldItalic.ttf": bi,
    };

    const fonts = {
        Times: {
            normal: "TimesNewRoman.ttf",
            bold: "TimesNewRoman-Bold.ttf",
            italics: "TimesNewRoman-Italic.ttf",
            bolditalics: "TimesNewRoman-BoldItalic.ttf",
        },
    };

    // продублируем в глобальные поля pdfMake (на всякий случай)
    pdfMake.vfs = { ...(pdfMake.vfs || {}), ...vfs };
    pdfMake.fonts = { ...(pdfMake.fonts || {}), ...fonts };

    TIMES_READY = true;
    TIMES_VFS = vfs;
    TIMES_FONTS = fonts;

    return { fonts, vfs };
};

export const usePdfCreate = () => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const token = useSelector((state) => state.auth.token);
    const details = useSelector((state) => state.details.data);
    const detailsColumns = useSelector((state) => state.details.columns);
    const updates = useSelector((state) => state.updates.data);
    const updateColumns = useSelector((state) => state.updates.columns);
    const user = useSelector((state) => state.user.data);
    const companyKey = user?.relationships?.rescue_company?.key;
    const logoPath = companyKey === "semser" ? "/img/semser.png" : "/img/pvass.png";

    useEffect(() => {
        if (token && id) {
            dispatch(fetchDetails({ departureId: id, token }));
            dispatch(fetchUpdates({ departureId: id, token }));
        }
    }, [dispatch, id, token]);

    const renderField = (value) => {
        if (value === null || value === undefined) return "Отсутствует";
        if (typeof value === "object") return JSON.stringify(value, null, 2);
        return value;
    };

    const buildRowsFromColumns = (columns, data, content, allowNested = false) => {
        if (!columns || !data) return [];

        return columns.reduce((rows, col) => {
            const key = col.key;
            const value = data[key];

            if (!Array.isArray(value)) {
                rows.push([
                    { text: col.alias || col.name, bold: true },
                    renderField(value),
                ]);
                return rows;
            }

            if (
                allowNested &&
                Array.isArray(value) &&
                value.length > 0 &&
                typeof value[0] === "object"
            ) {
                const tableHeader = Object.keys(value[0]);
                const tableBody = value.map((obj) =>
                    tableHeader.map((field) => renderField(obj[field]))
                );

                content.push({
                    text: col.alias || col.name,
                    style: "subheader",
                    margin: [0, 5, 0, 5],
                });

                content.push({
                    table: {
                        widths: Array(tableHeader.length).fill("*"),
                        body: [tableHeader, ...tableBody],
                    },
                    layout: "lightHorizontalLines",
                    margin: [0, 0, 0, 10],
                });

                return rows;
            }

            return rows;
        }, []);
    };

    const fetchImageBase64 = async (imagePath) => {
        try {
            const response = await fetch(imagePath);
            const blob = await response.blob();
            return await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            return null;
        }
    };

    const generatePdf = async () => {
        const attrs = details?.attributes ?? {};
        const { fonts, vfs } = await ensureTimes();
        const img = await fetchImageBase64(logoPath);

        const content = [];

        if (img) {
            content.push({
                image: img,
                width: 600,
                alignment: "center",
                margin: [0, 0, 0, 10],
            });
        }

        content.push({
            text: `Печатная форма донесения №${renderField(attrs.report_code)}`,
            style: "header",
            alignment: "center",
        });

        // Статическая часть
        const staticRows = buildRowsFromColumns(
            detailsColumns?.attributes,
            attrs,
            content,
            false
        );

        content.push({
            table: {
                widths: ["35%", "*"],
                body: staticRows,
            },
            layout: "lightHorizontalLines",
            margin: [0, 10, 0, 20],
        });

        // Обновления
        if (updates?.length) {
            content.push({ text: "Журнал", style: "sectionHeader" });

            updates.forEach((entry) => {
                updateColumns.forEach((col) => {
                    const value = entry[col.key];

                    if (Array.isArray(value)) {
                        content.push({
                            text: `${col.alias || col.name}:`,
                            style: "paragraph",
                            margin: [0, 6, 0, 6],
                        });

                        if (value.length === 0) {
                            content.push({
                                text: `Отсутствует`,
                                margin: [15, 0, 0, 2],
                            });
                        } else if (
                            typeof value[0] === "object" &&
                            "name" in value[0] &&
                            "count" in value[0]
                        ) {
                            value.forEach((item) => {
                                const name = item.name ?? "Без названия";
                                const count = item.count ?? "0";

                                content.push({
                                    text: `${name} – ${count}`,
                                    margin: [15, 0, 0, 1],
                                });
                            });
                        } else if (typeof value[0] === "object") {
                            value.forEach((item) => {
                                content.push({
                                    margin: [10, 2, 0, 1],
                                    bold: true,
                                });

                                Object.entries(item).forEach(([k, v]) => {
                                    content.push({
                                        text: `${k}: ${renderField(v)}`,
                                        margin: [20, 0, 0, 1],
                                    });
                                });
                            });
                        } else {
                            // массив примитивов
                            content.push({
                                text: renderField(value),
                                margin: [15, 0, 0, 1],
                            });
                        }

                        content.push({ text: "", margin: [0, 2, 0, 2] });
                    } else {
                        content.push({
                            text: `${col.alias || col.name}: ${renderField(value)}`,
                            margin: [0, 0, 0, 2],
                        });
                    }
                });

                content.push({ text: "", margin: [0, 5, 0, 5] });
            });
        }

        const docDefinition = {
            pageSize: "A4",
            pageMargins: [20, 20, 20, 20],
            defaultStyle: { font: "Times", fontSize: 10 },
            styles: {
                header: {
                    fontSize: 14,
                    bold: true,
                    margin: [0, 0, 0, 10],
                },
                sectionHeader: {
                    fontSize: 13,
                    bold: true,
                    margin: [0, 20, 0, 10],
                },
                subheader: {
                    fontSize: 11,
                    bold: true,
                    margin: [0, 10, 0, 5],
                },
                paragraph: {
                    margin: [0, 5, 0, 2],
                },
            },
            content,
        };

        try {
            pdfMake.createPdf(docDefinition, undefined, fonts, vfs).download(`Report_${renderField(attrs.report_code)}.pdf`);
        } catch (err) {
            console.error("Ошибка генерации PDF:", err);
        }
    };

    return {
        generatePdf,
        reportCode: details?.attributes?.report_code ?? "Нет данных",
        isClosed: details?.attributes?.is_closed,
    };
};